import {
  Box,
  Button,
  Grid,
  Typography,
  AppBar,
  Toolbar,
  useTheme,
  Autocomplete,
  TextField,
  Dialog,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import { useEffect, useState } from "react";
import {
  darkenColor,
  dataGridTexts,
  formatCnpj,
  nameAbbreviation,
} from "../../../../../utils";
import { xlsxGenerator } from "../../../../../utils/convert-to-xlsx/xslxGenerator";
import { useBackofficeService } from "../../../../../use-cases/backoffice";
import { useAppSelector } from "../../../../../application/states/hooks";

const resourceTypeToLabel = (type: string) => {
  switch (type) {
    case "export":
      return "Exportação";
    case "unit-search":
      return "Consulta unitária";
    case "enrichment":
      return "Enriquecimento";
    default:
      return "Desconhecido";
  }
};

export const BackofficeConsumptionPage = () => {
  const theme = useTheme();
  const companyList = useAppSelector(
    (state) => state.backofficeCompany.companyList
  );
  const selectedCompany = useAppSelector(
    (state) => state.backofficeCompany.selectedCompany
  );
  const consumptionReport = useAppSelector(
    (state) => state.backofficeConsumption.report
  )

  const { 
    getConsumptionReport,
    resetConsumptionReport,
    selectCompany 
  } = useBackofficeService();

  const { company } = useAppSelector((state) => state.company);
  const resaleConfig = useAppSelector(
    (state) => state.resaleConfig.resaleConfig
  );

  const [consumptionInfoStartDate, setConsumptionInfoStartDate] =
    useState<Date | null>(null);
  const [consumptionInfoEndDate, setConsumptionInfoEndDate] =
    useState<Date | null>(null);
  const [total, setTotal] = useState(0);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<
    (typeof companyList)[0] | null
  >(null);
  const [openConsumptionDialog, setOpenConsumptionDialog] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);

  useEffect(() => {
    let totalAux = 0;
    for (
      let i = 0;
      i < (consumptionReport?.consumptionsList.length ?? 0);
      i++
    ) {
      totalAux += consumptionReport?.consumptionsList[i].total ?? 0;
    }
    setTotal(totalAux);
  }, [consumptionReport?.consumptionsList]);

  // Sincroniza seleção local com Redux
  useEffect(() => {
    if (selectedCompany) {
      setSelectedCompanyOption(selectedCompany);
    } else {
      setSelectedCompanyOption(null);
    }
  }, [selectedCompany]);

  const consumptionReportCols: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "Data",
      headerAlign: "center",
      minWidth: 155,
      align: "center",
      renderCell(params) {
        return (
          <div>
            {new Date(params.value as string).toLocaleString("pt-BR", {
              timeZone: "America/Sao_Paulo",
            })}
          </div>
        );
      },
    },
    {
      field: "type",
      headerName: "Origem",
      headerAlign: "center",
      align: "center",
      minWidth: 140,
      renderCell(params) {
        return <div>{resourceTypeToLabel(params.value)}</div>;
      },
    },
    {
      field: "user",
      headerName: "Usuário",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return <div>{params.value?.name ?? ""}</div>;
      },
    },
    {
      field: "ip",
      headerName: "IP",
      flex: 0.5,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return <div>{params.value}</div>;
      },
    },
    {
      field: "totalSeparatedCredits",
      headerName: "Créditos separados",
      headerAlign: "center",
      minWidth: 150,
      align: "center",
      renderCell(params) {
        return <div>{params.value.toLocaleString("pt-BR")}</div>;
      },
    },
    {
      field: "totalSubscription",
      headerName: "Créditos do plano",
      headerAlign: "center",
      minWidth: 150,
      align: "center",
      renderCell(params) {
        return <div>{params.value.toLocaleString("pt-BR")}</div>;
      },
    },
    {
      field: "total",
      headerName: "Total",
      flex: 0.6,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return <div>{params.value.toLocaleString("pt-BR")}</div>;
      },
    },
  ];

  // Função para exportar XLSX (igual receita)
  const exportConsumptionReport = () => {
    if (!consumptionReport || !consumptionReport.consumptionsList.length)
      return;
    const mappedResult = consumptionReport.consumptionsList.map(
      (item, index) => {
        let totalSubscription = 0;
        let totalSeparatedCredits = 0;
        for (let i = 0; i < item.origins.length; i++) {
          totalSubscription += item.origins[i].subscription.total;
          totalSeparatedCredits += item.origins[i].separatedCredits.total;
        }
        return [
          new Date(item.createdAt).toLocaleString("pt-BR", {
            timeZone: "America/Sao_Paulo",
          }),
          resourceTypeToLabel(item.type),
          item.user?.name ?? "",
          item.ip,
          totalSeparatedCredits.toLocaleString("pt-BR"),
          totalSubscription.toLocaleString("pt-BR"),
          (totalSubscription + totalSeparatedCredits).toLocaleString("pt-BR"),
        ];
      }
    );
    const headers = [
      "Data",
      "Origem",
      "Usuário",
      "IP",
      "Créditos separados",
      "Créditos do plano",
      "Total",
    ];
    let primaryColor = "723989";
    let contrastColor = "FFFFFFFF";
    let fgColor = "e8e4d6";
    if (resaleConfig) {
      if (resaleConfig.id !== "") {
        if (resaleConfig.theme.colors.primary.main !== "") {
          primaryColor = resaleConfig.theme.colors.primary.main;
        }
        if (resaleConfig.theme.colors.primary.contrastColor !== "") {
          contrastColor = resaleConfig.theme.colors.primary.contrastColor;
        }
      }
    }
    xlsxGenerator(
      mappedResult,
      headers,
      "Relatório de Consumo",
      primaryColor,
      contrastColor,
      fgColor
    );
  };

  return (
    <Box>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Relatório de consumo
          </Typography>
          <Button
            color="inherit"
            onClick={() => {
              setConsumptionInfoStartDate(null);
              setConsumptionInfoEndDate(null);
              resetConsumptionReport();
              setReportGenerated(false);
            }}
          >
            Limpar filtros
          </Button>
        </Toolbar>
      </AppBar>
      <Box p={3}>
        <Grid container justifyContent={"center"} spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              options={companyList}
              getOptionLabel={(option) => {
                if (!option || !option.company) return "";
                return `${nameAbbreviation(option.company.razaoSocial)}${
                  option.company.nomeFantasia
                    ? ` / ${nameAbbreviation(option.company.nomeFantasia)}`
                    : ""
                } - ${formatCnpj(option.company.cnpj)}`;
              }}
              value={selectedCompanyOption}
              onChange={(_, newValue) => {
                setSelectedCompanyOption(newValue);
                if (newValue) {
                  selectCompany({
                    ...newValue,
                    resaleConfig: Array.isArray(newValue.resaleConfig)
                      ? newValue.resaleConfig[0]
                      : newValue.resaleConfig,
                  });
                } else {
                  selectCompany(undefined);
                }
                resetConsumptionReport();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione a empresa"
                  variant="outlined"
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option?.company?.id === value?.company?.id
              }
            />
          </Grid>
          <Grid item>
            <LocalizationProvider
              dateFormats={{
                fullDate: "dd/MM/yyyy",
              }}
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                value={consumptionInfoStartDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setConsumptionInfoStartDate(newValue);
                  }
                }}
                disableFuture
                views={["year", "month"]}
                label="Data inicial"
                slotProps={{
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: darkenColor(
                            theme.palette.primary.main,
                            10
                          ),
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <LocalizationProvider
              dateFormats={{
                fullDate: "dd/MM/yyyy",
              }}
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                value={consumptionInfoEndDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setConsumptionInfoEndDate(newValue);
                  }
                }}
                disableFuture
                views={["year", "month"]}
                label="Data final"
                slotProps={{
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: darkenColor(
                            theme.palette.primary.main,
                            10
                          ),
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                "&:hover": {
                  backgroundColor: darkenColor(theme.palette.primary.main, 10),
                },
                color: theme.palette.primary.contrastText,
              }}
              onClick={async () => {
                await getConsumptionReport({
                  startDate: consumptionInfoStartDate ?? undefined,
                  endDate: consumptionInfoEndDate ?? undefined,
                });
                setReportGenerated(true);
              }}
              //disabled={!selectedCompanyOption}
            >
              Gerar relatório
            </Button>
          </Grid>
          {/* Botões extras após gerar relatório */}
          {reportGenerated &&
            consumptionReport &&
            consumptionReport.consumptionsList.length > 0 && (
              <Grid item xs={12} container spacing={2} justifyContent="center">
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenConsumptionDialog(true)}
                  >
                    Abrir relatório de consumo
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={exportConsumptionReport}
                  >
                    Exportar XLSX
                  </Button>
                </Grid>
              </Grid>
            )}
          <Grid item xs={12}>
            <Typography style={{ fontSize: "1.1rem" }}>
              {consumptionReport ? (
                consumptionReport?.consumptionsList.length > 0 ? (
                  <>
                    Total sintetizado:{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {total.toLocaleString("pt-BR")}
                    </span>{" "}
                    |{" Plano"}:{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {consumptionReport?.subscriptionData?.usage?.type ?? ""}
                    </span>
                  </>
                ) : null
              ) : null}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {/* MODAL DE RELATÓRIO DE CONSUMO */}
      <Dialog
        fullScreen
        open={openConsumptionDialog}
        onClose={() => setOpenConsumptionDialog(false)}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Relatório de Consumo
            </Typography>
            <Button
              color="inherit"
              onClick={() => setOpenConsumptionDialog(false)}
            >
              FECHAR
            </Button>
          </Toolbar>
        </AppBar>
        <Box
          pt={2.5}
          sx={{ overflow: "auto", maxHeight: "calc(100vh - 64px)" }}
        >
          <Grid container justifyContent={"center"}>
            <Grid item xs={12}>
              {/* RESUMO */}
              <Typography style={{ fontSize: "1.1rem", marginBottom: 16 }}>
                {consumptionReport &&
                  consumptionReport?.consumptionsList.length > 0 && (
                    <>
                      Total sintetizado:{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {total.toLocaleString("pt-BR")}
                      </span>{" "}
                      |{" Plano"}:{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {consumptionReport?.subscriptionData?.usage?.type ?? ""}
                      </span>
                    </>
                  )}
              </Typography>
              {/* TABELA */}
              {consumptionReport &&
                consumptionReport?.consumptionsList.length > 0 && (
                  <DataGrid
                    showCellVerticalBorder
                    disableColumnFilter
                    disableColumnMenu
                    rowSelection={false}
                    autoHeight={true}
                    localeText={dataGridTexts}
                    pagination={undefined}
                    rows={
                      consumptionReport.consumptionsList.map((item, index) => {
                        let totalSubscription = 0;
                        let totalSeparatedCredits = 0;
                        for (let i = 0; i < item.origins.length; i++) {
                          totalSubscription +=
                            item.origins[i].subscription.total;
                          totalSeparatedCredits +=
                            item.origins[i].separatedCredits.total;
                        }
                        return {
                          id: index,
                          createdAt: item.createdAt,
                          type: item.type,
                          user: item.user,
                          ip: item.ip,
                          totalSeparatedCredits: totalSeparatedCredits,
                          totalSubscription: totalSubscription,
                          total: totalSubscription + totalSeparatedCredits,
                        };
                      }) ?? []
                    }
                    columns={consumptionReportCols}
                    sx={{
                      "& .MuiDataGrid-columnHeader": {
                        backgroundColor: theme.palette.primary.main,
                        color:
                          company?.resale === true || company?.resaleId
                            ? theme.palette.primary.contrastText
                            : "#FFF",
                      },
                      "& .MuiDataGrid-cell": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                      "& .MuiDataGrid-cell:focus": {
                        outline: 0,
                      },
                      "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                      },
                      minWidth: "100%",
                    }}
                  />
                )}
            </Grid>
          </Grid>
        </Box>
      </Dialog>
    </Box>
  );
};